/**
 * Creating and editing a referrer or a clinic (docs/ux/intake.md). Both were
 * seeded read-only lists, so a referral from a firm the seed does not know
 * could not be recorded at all.
 *
 * The id is derived from the name and is never shown as a field. It is the key
 * every case refers to, and a hand-typed key is a spelling mistake waiting to
 * split one referrer into two. On an edit it does not change, because cases
 * already point at it.
 */

import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { ApiError, reasonOf } from '@/app/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ClinicRecord, ReferrerRecord } from '@/app/types';
import { useCreateClinic, useCreateReferrer, useUpdateClinic, useUpdateReferrer } from './useIntake';

export function ReferrerDialog({ open, onOpenChange, existing }: { open: boolean; onOpenChange: (open: boolean) => void; existing?: ReferrerRecord | null }) {
  const id = useId();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'standard' | 'plaintiff'>('standard');
  const [slaDays, setSlaDays] = useState('21');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateReferrer();
  const update = useUpdateReferrer();
  const busy = create.isPending || update.isPending;

  useEffect(() => {
    if (!open) return;
    setName(existing?.name ?? '');
    setKind(existing?.kind ?? 'standard');
    setSlaDays(String(existing?.slaDays ?? 21));
    setError(null);
  }, [open, existing]);

  async function submit() {
    setError(null);
    if (name.trim().length === 0) { setError('A name is required.'); return; }
    const days = Number(slaDays);
    if (!Number.isInteger(days) || days < 1 || days > 365) { setError('The days allowed is a whole number between 1 and 365.'); return; }
    try {
      if (existing) await update.mutateAsync({ id: existing.id, name: name.trim(), kind, slaDays: days });
      else await create.mutateAsync({ name: name.trim(), kind, slaDays: days });
      toast.success(`${name.trim()} ${existing ? 'updated' : 'added'}.`);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? 'A referrer with that name exists.' : reasonOf(err) ?? 'The referrer could not be saved.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-slot="referrer-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit referrer' : 'New referrer'}</DialogTitle>
          <DialogDescription>
            {existing ? 'The name and terms change; the key the cases point at does not.' : 'A plaintiff referrer is the one the payment gate applies to.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-name`}>Name</Label>
            <Input id={`${id}-name`} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-kind`}>Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as 'standard' | 'plaintiff')}>
              <SelectTrigger id={`${id}-kind`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard</SelectItem>
                <SelectItem value="plaintiff">Plaintiff</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-sla`}>Days allowed</Label>
            <Input id={`${id}-sla`} inputMode="numeric" value={slaDays} onChange={(e) => setSlaDays(e.target.value)} />
          </div>
        </div>
        {error ? <p role="alert" className="text-[13px] text-beam-error">{error}</p> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button type="button" data-primary="true" onClick={() => void submit()} disabled={busy}>{busy ? 'Saving' : existing ? 'Save' : 'Add'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ClinicDialog({ open, onOpenChange, existing }: { open: boolean; onOpenChange: (open: boolean) => void; existing?: ClinicRecord | null }) {
  const id = useId();
  const [name, setName] = useState('');
  const [suburb, setSuburb] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateClinic();
  const update = useUpdateClinic();
  const busy = create.isPending || update.isPending;

  useEffect(() => {
    if (!open) return;
    setName(existing?.name ?? '');
    setSuburb(existing?.suburb ?? '');
    setError(null);
  }, [open, existing]);

  async function submit() {
    setError(null);
    if (name.trim().length === 0 || suburb.trim().length === 0) { setError('A name and a suburb are required.'); return; }
    try {
      if (existing) await update.mutateAsync({ id: existing.id, name: name.trim(), suburb: suburb.trim() });
      else await create.mutateAsync({ name: name.trim(), suburb: suburb.trim() });
      toast.success(`${name.trim()} ${existing ? 'updated' : 'added'}.`);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? 'A clinic with that name exists.' : reasonOf(err) ?? 'The clinic could not be saved.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-slot="clinic-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit clinic' : 'New clinic'}</DialogTitle>
          <DialogDescription>{existing ? 'The name and suburb change; the key the appointments point at does not.' : 'Where Beam books appointments.'}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-name`}>Name</Label>
            <Input id={`${id}-name`} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-suburb`}>Suburb</Label>
            <Input id={`${id}-suburb`} value={suburb} onChange={(e) => setSuburb(e.target.value)} />
          </div>
        </div>
        {error ? <p role="alert" className="text-[13px] text-beam-error">{error}</p> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button type="button" data-primary="true" onClick={() => void submit()} disabled={busy}>{busy ? 'Saving' : existing ? 'Save' : 'Add'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
